import { getAuthHeader } from '../auth'
import { API_URL } from './base'
import { SSEClient } from '../sseClient'
export type { SSEClient }

// Streaming Event Types
export interface StreamEvent {
  type: 'init' | 'progress' | 'complete' | 'error' | 'workflow_created' | 'tool_use' | 'tool_result' | 'log'
  content: string
  timestamp: string
  data?: Record<string, unknown>
}

export type StreamEventHandler = (event: StreamEvent) => void
export type StreamErrorHandler = (error: Error) => void

// Fetch a short-lived, single-use SSE ticket from the backend.
// The ticket replaces the long-lived JWT in the URL query string so the
// JWT is never exposed in browser history or proxy logs.
export async function fetchSseTicket(): Promise<string> {
  const response = await fetch(`${API_URL}/api/v1/auth/sse-ticket`, {
    method: 'POST',
    headers: { ...getAuthHeader() },
  })
  if (!response.ok) throw new Error('Failed to obtain SSE ticket')
  const data = await response.json()
  return data.ticket as string
}

/**
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * SSE STREAMING PATTERNS
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * DashboardOS uses TWO distinct streaming patterns:
 *
 * 1. **EventSource (SSE)** — Server-Sent Events with automatic reconnection
 *    - Use: `useSSEWithReconnection()` hook in React components
 *    - Use: `SSEClient` class directly in non-React contexts (e.g., Zustand stores)
 *    - Examples: Workflow execution, agent scans, profile scans
 *    - Features: Exponential backoff, single-use tickets, state tracking
 *
 * 2. **fetch() with ReadableStream** — Manual streaming for request/response
 *    - Use: `AbortController + fetch()` with streaming response
 *    - Examples: Chat messages, thinker responses
 *    - Features: Manual abort, no reconnection (one-shot request)
 *
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */

// ──────────────────────────────────────────────────────────────────────────────
// URL Builders for SSE Endpoints
// ──────────────────────────────────────────────────────────────────────────────
// These functions build SSE endpoint URLs. Consumers should use:
// - `useSSEWithReconnection()` in React components
// - `SSEClient` directly in Zustand stores or non-React contexts
// ──────────────────────────────────────────────────────────────────────────────

export interface AgentScanParams {
  prompt: string
  repository_path?: string
  mode?: string
  focus_area?: string
  scanner?: string
  agent_definition_id?: number
}

/**
 * Build URL for agent scan SSE endpoint
 */
export function buildAgentScanStreamUrl(params: AgentScanParams): string {
  const searchParams = new URLSearchParams({
    prompt: params.prompt,
    repository_path: params.repository_path || '.',
    mode: params.mode || 'explore',
  })
  if (params.focus_area) searchParams.append('focus_area', params.focus_area)
  if (params.scanner) searchParams.append('scanner', params.scanner)
  if (params.agent_definition_id) searchParams.append('agent_definition_id', String(params.agent_definition_id))

  return `${API_URL}/workflows/agent-scan/stream?${searchParams.toString()}`
}

/**
 * Build URL for profile scan SSE endpoint
 */
export function buildProfileScanStreamUrl(profileId: string, repositoryPath: string = '.'): string {
  const searchParams = new URLSearchParams({
    profile_id: profileId,
    repository_path: repositoryPath,
  })

  return `${API_URL}/workflows/scan/profile/stream?${searchParams.toString()}`
}

/**
 * Build URL for workflow execution SSE endpoint
 */
export function buildWorkflowExecutionStreamUrl(
  workflowId: number,
  executor: string = 'claude',
  permissionMode: string = 'acceptEdits'
): string {
  const searchParams = new URLSearchParams({
    executor,
    permission_mode: permissionMode,
  })

  return `${API_URL}/workflows/${workflowId}/execute/stream?${searchParams.toString()}`
}

// ──────────────────────────────────────────────────────────────────────────────
// Global Event Bus (unified per-user SSE stream)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Envelope type for all events delivered through the unified global SSE stream.
 * Each event from GET /api/events/stream has this shape in addition to its
 * source-specific payload fields.
 */
export interface GlobalEventEnvelope {
  seq: number
  source: 'chat' | 'workflow' | 'scan' | 'orchestrator' | 'thinker' | 'enrichment'
  source_id: number
  type: string
  [key: string]: unknown
}

/**
 * Open the unified per-user SSE stream at GET /api/events/stream.
 *
 * The `urlFactory` reads `since` from localStorage on each reconnect attempt
 * so the client always resumes from the most recently persisted seq without
 * needing to hold a stale closure value.
 *
 * @param getStoredSince  Called on each (re)connect to obtain the ?since= value.
 * @param onEvent         Invoked for every deserialized GlobalEventEnvelope.
 * @returns               The SSEClient instance (already connected).
 */
export function connectGlobalEventStream(
  getStoredSince: () => number,
  onEvent: (envelope: GlobalEventEnvelope) => void,
): SSEClient<GlobalEventEnvelope & { content: string; timestamp: string }> {
  const urlFactory = () => {
    const since = getStoredSince()
    return `${API_URL}/api/events/stream?since=${since}`
  }

  const client = new SSEClient<GlobalEventEnvelope & { content: string; timestamp: string }>(
    // Static url arg is unused when urlFactory is provided, but constructor requires it.
    `${API_URL}/api/events/stream`,
    {
      urlFactory,
      fetchSseTicket: fetchSseTicket,
      maxRetries: 20,
      initialDelay: 1000,
      maxDelay: 30000,
      persistent: true,
    }
  )

  client.addEventListener(onEvent as (event: GlobalEventEnvelope & { content: string; timestamp: string }) => void)
  client.connect()
  return client
}
